import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import "./App.css";
import Login from "@features/auth/components/Login";
import { BrowserRouter } from "react-router-dom";
import useToken from "@/hooks/useToken";
import { withTranslation } from "react-i18next";
import { AccountContext } from "./context/AccountContext";
import { NotificationProvider } from "./context/NotificationContext";
import { useTranslation } from "react-i18next";
import { getBrandingSettings } from "./config/branding";
import {
  applyTenantBranding,
  fetchTenantBranding,
  getCachedTenantBranding,
  getTenantId,
  rememberTenantBranding,
  removeTenantBranding,
} from "./config/tenantBranding";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "./styles/theme.tsx";
import AppRoutes from "./routes/AppRoutes";
import Alerts from "./components/ui/Alerts";
import { SESSION_EXPIRED_FLAG } from "./utils/http";
import { useVersionCheck } from "./hooks/useVersionCheck";

const DOMAIN_BRAND = getBrandingSettings(window.location.hostname);

// A brand with its own domain (medfit, …) always wins over a tenant link.
const HAS_DOMAIN_BRAND = DOMAIN_BRAND.name !== "sophyapp";

// How long a first visit from a tenant link waits for its branding before
// falling back to sophyapp.
const TENANT_WAIT_MS = 3000;

// Tenants run on the sophyapp domains, so towards the backend they keep the
// "sophyapp" style; the backend finds their branding through the account.
const tenantBrand = (tenant) => ({
  ...DOMAIN_BRAND,
  displayName: tenant.name,
  imagePath: tenant.logo_url || DOMAIN_BRAND.imagePath,
  title: tenant.name,
  icon: tenant.favicon_url || tenant.logo_url || DOMAIN_BRAND.icon,
  portalUrl: tenant.portal_url,
});

function App() {
  useVersionCheck();
  const [tenant, setTenant] = useState(() =>
    HAS_DOMAIN_BRAND ? null : getCachedTenantBranding()
  );
  // First visit from a tenant link: nothing cached yet, so show a neutral
  // spinner instead of flashing the sophyapp login until the branding arrives.
  const [waitingForTenant, setWaitingForTenant] = useState(
    () => !HAS_DOMAIN_BRAND && Boolean(getTenantId()) && !tenant
  );
  const brand = useMemo(
    () => (tenant ? tenantBrand(tenant) : DOMAIN_BRAND),
    [tenant]
  );
  const isBrandingCustomer = HAS_DOMAIN_BRAND || Boolean(tenant);

  const applicationName = brand.name;
  const brandName = brand.displayName || brand.name;
  const applicationImagePath = brand.imagePath;
  const [muiTheme, setMuiTheme] = useState(createAppTheme);
  const { i18n } = useTranslation();
  const { token, setToken } = useToken();

  // Initialize userData, checking for fresh data from URL redirect
  const getInitialUserData = () => {
    // Check if we just received data via URL (stored by useToken hook)
    const storedData = localStorage.getItem("userData");
    return storedData ? JSON.parse(storedData) : null;
  };

  const [userData, setUserData] = useState(getInitialUserData());
  const [sessionExpiredAlertOpen, setSessionExpiredAlertOpen] = useState(false);

  // After login the account's tenant wins over the one from the link; an
  // account without one (branding: null) goes back to plain sophyapp.
  const handleLogin = (data) => {
    if (!HAS_DOMAIN_BRAND && data && "branding" in data) {
      rememberTenantBranding(data.branding);
      setTenant(data.branding);
    }
    setUserData(data);
  };

  useEffect(() => {
    if (localStorage.getItem(SESSION_EXPIRED_FLAG) === "1") {
      setSessionExpiredAlertOpen(true);
      localStorage.removeItem(SESSION_EXPIRED_FLAG);
    }
  }, []);

  // The cache paints the first frame; refresh it in case the tenant changed
  // its branding or switched it off.
  useEffect(() => {
    const tenantId = getTenantId();
    if (HAS_DOMAIN_BRAND || !tenantId) {
      return;
    }
    const stopWaiting = () => setWaitingForTenant(false);
    const timeout = setTimeout(stopWaiting, TENANT_WAIT_MS);
    fetchTenantBranding(tenantId)
      .then(setTenant)
      .catch(() => {})
      .finally(() => {
        clearTimeout(timeout);
        stopWaiting();
      });
    return () => clearTimeout(timeout);
  }, []);

  // Layout effect: the tenant colours are in place before the first paint.
  useLayoutEffect(() => {
    if (HAS_DOMAIN_BRAND) {
      return;
    }
    if (tenant) {
      applyTenantBranding(tenant);
    } else {
      removeTenantBranding();
    }
    setMuiTheme(createAppTheme());
  }, [tenant]);

  useEffect(() => {
    const cssPath = brand.css;
    import(`${cssPath}`).then(() => setMuiTheme(createAppTheme()));
    document.title = brand.title;

    var link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = brand.icon;

    if (userData && userData.language) {
      i18n.changeLanguage(userData.language);
    }
  }, [userData, brand, i18n]);

  if (waitingForTenant) {
    return (
      <div className="tenant-loading" role="status" aria-label="Laden">
        <div className="tenant-loading-spinner" />
      </div>
    );
  }

  if (!token) {
    return (
      <>
        <Login
          setToken={setToken}
          setUserData={handleLogin}
          application={applicationName}
          brandName={brandName}
          imagePath={applicationImagePath}
          isBrandingCustomer={isBrandingCustomer}
          patientPortalUrl={brand.portalUrl}
          tenant={tenant}
        />
        <Alerts
          open={sessionExpiredAlertOpen}
          message="Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an."
          severity="warning"
          onClose={() => setSessionExpiredAlertOpen(false)}
        />
      </>
    );
  }

  return (
    <MuiThemeProvider theme={muiTheme}>
      <AccountContext.Provider
        value={{
          userData,
          applicationName,
          applicationImagePath,
          brandName,
          isBrandingCustomer,
        }}
      >
        <NotificationProvider>
          <div className="main_background">
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </div>
        </NotificationProvider>
      </AccountContext.Provider>
    </MuiThemeProvider>
  );
}

export default withTranslation()(App);
